/**
 * LevelFunnelWidget — «Воронки уровней» (вкладка раздела «Прогрессия»)
 * ========================================================================
 *
 * Контейнер: локальные контролы (режим, диапазон уровней, горизонт, галки
 * инсталлов/абсолютов, N бустеров) + подвкладки «Таблица» / «Графики».
 * Прецедент по структуре — StartFunnelWidget.js.
 *
 * Когортная семантика (D1 плана «Воронки уровней»): диапазон дат дашборда —
 * дата регистрации (acc_reg_ts), горизонт наблюдения задаётся тут же.
 */

import React, { useDeferredValue, useMemo, useState } from 'react';
import { BaseWidget, useBaseWidget } from './BaseWidget';
import { useWidgetData } from '../../hooks/useWidgetData';
import { useFilters } from '../../hooks/useFilters';
import { useLevelFunnelPrefetch } from '../../hooks/useLevelFunnelPrefetch';
import { MetricsService } from '../../services/metricsService';
import { withDefaultDateRange } from '../../utils/defaultDateRange';
import { ToggleGroup, ToggleGroupItem } from '../ui/toggle-group';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { LevelFunnelTable } from './levelFunnel/LevelFunnelTable';
import { LevelFunnelCharts } from './levelFunnel/LevelFunnelCharts';
import { AbsorptionTimingCharts } from './levelFunnel/AbsorptionTimingCharts';
import { sliceDataset } from './levelFunnel/slice';
import { levelFunnel as levelFunnelSpecs } from '@ggstat/chart-specs';

// Режимы, горизонты, диапазоны уровней — общие с MCP (shared/chart-specs/specs/levelFunnel.json).
const CONTROLS = levelFunnelSpecs.controls;
const MODES = CONTROLS.modes;
// Дефолтные диапазоны уровней по режиму (P0, artifacts/level-funnels-events.md).
const DEFAULT_LEVEL_RANGE = CONTROLS.defaultLevelRange;
const LEVEL_MIN = CONTROLS.levelMin;
const LEVEL_MAX = CONTROLS.levelMax;
const HORIZONS = CONTROLS.horizons;
const BOOSTER_OPTIONS = Array.from({ length: CONTROLS.boosterNMax }, (_, i) => i + 1);

/** Ограничивает диапазон уровней и чинит от > до, не давая улететь за 1..2000. */
function clampLevelRange(from, to) {
  const clamp = (v) => Math.min(LEVEL_MAX, Math.max(LEVEL_MIN, Number(v) || LEVEL_MIN));
  let f = clamp(from);
  let t = clamp(to);
  if (f > t) t = f;
  return [f, t];
}

export function LevelFunnelWidget() {
  const [mode, setMode] = useState('campaign');
  const [levelRange, setLevelRange] = useState(DEFAULT_LEVEL_RANGE.campaign);
  const [levelDraft, setLevelDraft] = useState(DEFAULT_LEVEL_RANGE.campaign.map(String));
  const [horizon, setHorizon] = useState(CONTROLS.defaultHorizon);
  const [fromInstalls, setFromInstalls] = useState(false);
  const [showAbs, setShowAbs] = useState(false);
  const [boosterN, setBoosterN] = useState(CONTROLS.defaultBoosterN);
  const [subTab, setSubTab] = useState('table');

  // Дефолты диапазона уровней переключаются вместе с режимом (§1 плана).
  const handleModeChange = (value) => {
    if (!value) return;
    setMode(value);
    const range = DEFAULT_LEVEL_RANGE[value] || [1, 60];
    setLevelRange(range);
    setLevelDraft(range.map(String));
  };

  const [levelFrom, levelTo] = levelRange;

  // Поля держат сырой текст, пока пользователь печатает: поправка на каждое
  // нажатие (пустое → 1, «от > до» → подтянуть «до») не давала набрать число
  // вроде 100 через промежуточное «1». Валидное значение применяется сразу,
  // перекрёстная нормализация границ — только на blur/Enter.
  const handleLevelInput = (idx, raw) => {
    setLevelDraft((d) => (idx === 0 ? [raw, d[1]] : [d[0], raw]));
    const n = Number(raw);
    if (raw === '' || !Number.isInteger(n) || n < LEVEL_MIN || n > LEVEL_MAX) return;
    setLevelRange((r) => (idx === 0 ? [n, r[1]] : [r[0], n]));
  };

  const commitLevelDraft = () => {
    const next = clampLevelRange(levelRange[0], levelRange[1]);
    setLevelRange(next);
    setLevelDraft(next.map(String));
  };

  // Сеть только при смене режима (или фильтров дашборда — dateRange/platform,
  // это ловит сам useWidgetData через свою зависимость от filters). Горизонт,
  // диапазон уровней, «от инсталлов» и N бустеров режутся ниже в useMemo.
  const widgetData = useWidgetData(
    async (filters, options) => {
      const scoped = withDefaultDateRange(filters);
      return MetricsService.getLevelFunnelDataset(scoped, mode, options);
    },
    [mode]
  );

  const widgetProps = useBaseWidget(widgetData);
  const { data: dataset } = widgetProps;
  const { filters: dashboardFilters } = useFilters();

  useLevelFunnelPrefetch(true, mode);

  // Диапазон уровней — единственный контрол, где пользователь может быстро
  // печатать (два числовых поля); откладываем его для useMemo среза, чтобы
  // перерисовка 200-строчной таблицы не блокировала ввод (C5 плана).
  const deferredLevelFrom = useDeferredValue(levelFrom);
  const deferredLevelTo = useDeferredValue(levelTo);

  // Фильтры кампании/проекта/источника таблица событий не поддерживает
  // (см. metricsService.getLevelFunnelDataset) — droppedFilters считаем тут,
  // по фильтрам дашборда, а не из ответа сервера (в контракте этого поля
  // больше нет, см. план фазы B1).
  const droppedFilters = Boolean(
    (dashboardFilters?.project && dashboardFilters.project !== 'all') ||
      dashboardFilters?.campaigns?.length ||
      dashboardFilters?.campaign ||
      dashboardFilters?.mediaSource
  );

  // С фильтром версии сервер не отдаёт базу инсталлов (AppsFlyer без версий):
  // режим «от инсталлов» недоступен, вместо чекбокса — заглушка.
  const installsBaseAvailable = dataset?.installs?.installsBaseAvailable !== false;
  const effectiveFromInstalls = fromInstalls && installsBaseAvailable;

  const { tableData, timingData } = useMemo(
    () =>
      sliceDataset(
        dataset,
        {
          mode,
          horizon,
          levelFrom: deferredLevelFrom,
          levelTo: deferredLevelTo,
          fromInstalls: effectiveFromInstalls,
          boosterN
        },
        droppedFilters
      ),
    [dataset, mode, horizon, deferredLevelFrom, deferredLevelTo, effectiveFromInstalls, boosterN, droppedFilters]
  );

  // Подпись строится по ФАКТИЧЕСКИМ границам из ответа, а не по тому, что
  // набрал пользователь: сервер обрезает диапазон шире 200 уровней, и молчать
  // об этом нельзя — иначе «дальше данных нет» не отличить от «дальше не считали».
  const shownFrom = tableData?.levelFrom ?? levelFrom;
  const shownTo = tableData?.levelTo ?? levelTo;
  const rangeNote = tableData?.levelClamped
    ? `диапазон обрезан до ${shownFrom}–${shownTo} (максимум 200 уровней за раз)`
    : null;
  const installsNote = tableData?.installs?.droppedFilters
    ? 'инсталлы считаются без фильтров кампании/проекта/источника — таблица событий их не поддерживает'
    : null;

  const controls = (
    <div className="flex flex-wrap items-end gap-4">
      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">Режим</Label>
        <ToggleGroup type="single" value={mode} onValueChange={handleModeChange}>
          {MODES.map((m) => (
            <ToggleGroupItem key={m.value} value={m.value} aria-label={m.label}>
              {m.label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>

      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">Уровни от / до</Label>
        <div className="flex items-center gap-1">
          <Input
            type="number"
            className="w-20 h-9"
            min={LEVEL_MIN}
            max={LEVEL_MAX}
            value={levelDraft[0]}
            onChange={(e) => handleLevelInput(0, e.target.value)}
            onBlur={commitLevelDraft}
            onKeyDown={(e) => e.key === 'Enter' && commitLevelDraft()}
          />
          <span className="text-muted-foreground">—</span>
          <Input
            type="number"
            className="w-20 h-9"
            min={LEVEL_MIN}
            max={LEVEL_MAX}
            value={levelDraft[1]}
            onChange={(e) => handleLevelInput(1, e.target.value)}
            onBlur={commitLevelDraft}
            onKeyDown={(e) => e.key === 'Enter' && commitLevelDraft()}
          />
        </div>
      </div>

      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">Горизонт наблюдения</Label>
        <Select value={horizon} onValueChange={setHorizon}>
          <SelectTrigger className="w-[130px] h-9 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {HORIZONS.map((h) => (
              <SelectItem key={h.value} value={h.value}>
                {h.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {mode === 'absorption' && (
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">N бустеров</Label>
          <Select value={String(boosterN)} onValueChange={(v) => setBoosterN(Number(v))}>
            <SelectTrigger className="w-[80px] h-9 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {BOOSTER_OPTIONS.map((n) => (
                <SelectItem key={n} value={String(n)}>
                  {n}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {installsBaseAvailable ? (
        <label className="flex items-center gap-2 text-xs h-9">
          <Checkbox checked={fromInstalls} onCheckedChange={(v) => setFromInstalls(!!v)} />
          Считать от инсталлов
        </label>
      ) : (
        <span className="text-xs text-muted-foreground h-9 flex items-center">
          «Считать от инсталлов»: Недоступно с фильтром версии
        </span>
      )}

      <label className="flex items-center gap-2 text-xs h-9">
        <Checkbox checked={showAbs} onCheckedChange={(v) => setShowAbs(!!v)} />
        Показывать абсолютные числа
      </label>
    </div>
  );

  return (
    <BaseWidget
      {...widgetProps}
      title="Воронки уровней (когорта)"
      subtitle={[
        `Когорта по дате регистрации · уровни ${shownFrom}–${shownTo} · горизонт ${
          horizon === 'all' ? 'всё время' : `${horizon} дн`
        }`,
        rangeNote,
        installsNote
      ]
        .filter(Boolean)
        .join(' · ')}
      headerAction={null}
      emptyMessage="Нет данных за выбранную когорту"
      skeletonRows={10}
    >
      <div className="space-y-4">
        {controls}

        <Tabs value={subTab} onValueChange={setSubTab}>
          <TabsList>
            <TabsTrigger value="table">Таблица</TabsTrigger>
            <TabsTrigger value="charts">Графики</TabsTrigger>
          </TabsList>
          <TabsContent value="table">
            <LevelFunnelTable tableData={tableData} mode={mode} showAbs={showAbs} />
          </TabsContent>
          <TabsContent value="charts">
            <div className="space-y-8">
              <LevelFunnelCharts tableData={tableData} mode={mode} showAbs={showAbs} />
              {mode === 'absorption' && (
                <AbsorptionTimingCharts
                  tableData={tableData}
                  timingData={timingData}
                  showAbs={showAbs}
                  boosterN={boosterN}
                />
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </BaseWidget>
  );
}

export default LevelFunnelWidget;
